import React, { useState } from 'react';

const ClinicAnalytics = () => {
  // Tabs + view state
  const [activeTab, setActiveTab] = useState(0);
  const [view, setView] = useState('grid'); // 'grid' | 'detail'
  const [anim, setAnim] = useState('idle'); // 'idle' | 'leaving' | 'entering'
  const [selectedReport, setSelectedReport] = useState(null);

  // Static detailed data for some report types
  const reportDetails = {
    'Patient Demographics': {
      title: 'Patient Demographics Analysis',
      metrics: [
        { label: 'Total Patients', value: '1,160', change: '+12%' },
        { label: 'New Registrations', value: '245', change: '+8%' },
        { label: 'Average Age', value: '38.5', change: '-2%' },
      ],
      // Pie-like legend + counts (CSS visualization)
      chartData: [
        { name: '0-18', value: 120, color: '#8884d8' },
        { name: '19-35', value: 380, color: '#82ca9d' },
        { name: '36-50', value: 290, color: '#ffc658' },
        { name: '51-65', value: 200, color: '#8dd1e1' },
        { name: '65+', value: 150, color: '#d084d0' },
      ],
      tableData: [
        { demographic: 'Male', count: 640, percentage: '55.2%' },
        { demographic: 'Female', count: 500, percentage: '43.1%' },
        { demographic: 'Other', count: 20, percentage: '1.7%' },
        { demographic: 'Urban', count: 840, percentage: '72.4%' },
        { demographic: 'Rural', count: 320, percentage: '27.6%' },
      ],
      chartKind: 'pie',
    },
    'Patient Volume': {
      title: 'Patient Volume Trends',
      metrics: [
        { label: 'Today\'s Visits', value: '47', change: '+23%' },
        { label: 'Peak Hour', value: '2-3 PM', change: '52 visits' },
        { label: 'Avg Wait Time', value: '18 min', change: '-5%' },
      ],
      chartData: [
        { month: 'Jan', visits: 890 },
        { month: 'Feb', visits: 920 },
        { month: 'Mar', visits: 1100 },
        { month: 'Apr', visits: 980 },
        { month: 'May', visits: 1250 },
        { month: 'Jun', visits: 1180 },
      ],
      tableData: [
        { period: 'Last 7 Days', visits: 284, dailyAvg: 40.6, growth: '+15%' },
        { period: 'Last 30 Days', visits: 1180, dailyAvg: 39.3, growth: '+12%' },
        { period: 'Last 90 Days', visits: 3420, dailyAvg: 38.0, growth: '+8%' },
        { period: 'Last 365 Days', visits: 13650, dailyAvg: 37.4, growth: '+22%' },
      ],
      chartKind: 'bar',
    },
    'Revenue by Source': {
      title: 'Revenue Source Analysis',
      metrics: [
        { label: 'Total Revenue', value: '₹10,00,000', change: '+6.8%' },
        { label: 'Avg Per Patient', value: '₹862', change: '+4.2%' },
        { label: 'Collection Rate', value: '94.5%', change: '+2.1%' },
      ],
      chartData: [
        { source: 'Consultations', revenue: 450000 },
        { source: 'Pharmacy', revenue: 320000 },
        { source: 'Lab Tests', revenue: 180000 },
        { source: 'Packages', revenue: 50000 },
      ],
      tableData: [
        { source: 'Consultations', thisMonth: '₹4,50,000', lastMonth: '₹4,20,000', growth: '+7.1%' },
        { source: 'Pharmacy', thisMonth: '₹3,20,000', lastMonth: '₹3,10,000', growth: '+3.2%' },
        { source: 'Lab Tests', thisMonth: '₹1,80,000', lastMonth: '₹1,65,000', growth: '+9.1%' },
        { source: 'Packages', thisMonth: '₹50,000', lastMonth: '₹45,000', growth: '+11.1%' },
      ],
      chartKind: 'bar',
    },
    'Stock Status Report': {
      title: 'Pharmacy Stock Analysis',
      metrics: [
        { label: 'Total Items', value: '570', change: '-2%' },
        { label: 'Stock Value', value: '₹2,45,000', change: '+5%' },
        { label: 'Turnover Rate', value: '8.2x', change: '+12%' },
      ],
      chartData: [
        { category: 'In Stock', count: 450, color: '#10b981' },
        { category: 'Low Stock', count: 85, color: '#f59e0b' },
        { category: 'Out of Stock', count: 23, color: '#ef4444' },
        { category: 'Expired', count: 12, color: '#6b7280' },
      ],
      tableData: [
        { medicine: 'Paracetamol 500mg', stock: 250, reorderLevel: 100, status: 'In Stock', expiry: 'Dec 2025' },
        { medicine: 'Amoxicillin 250mg', stock: 45, reorderLevel: 50, status: 'Low Stock', expiry: 'Nov 2025' },
        { medicine: 'Aspirin 75mg', stock: 0, reorderLevel: 75, status: 'Out of Stock', expiry: '-' },
        { medicine: 'Ibuprofen 400mg', stock: 180, reorderLevel: 60, status: 'In Stock', expiry: 'Jan 2026' },
      ],
      chartKind: 'pie',
    },
    'Test Volume Report': {
      title: 'Laboratory Test Volume',
      metrics: [
        { label: 'Tests Today', value: '47', change: '+18%' },
        { label: 'Pending Reports', value: '12', change: '-25%' },
        { label: 'Avg TAT', value: '4.2 hrs', change: '-15%' },
      ],
      chartData: [
        { test: 'Blood Sugar', count: 120 },
        { test: 'CBC', count: 95 },
        { test: 'Lipid Profile', count: 78 },
        { test: 'Liver Function', count: 65 },
        { test: 'Kidney Function', count: 52 },
      ],
      tableData: [
        { test: 'Blood Sugar (FBS/PPBS)', daily: 12, weekly: 85, monthly: 320, revenue: '₹48,000' },
        { test: 'Complete Blood Count', daily: 9, weekly: 65, monthly: 245, revenue: '₹24,500' },
        { test: 'Lipid Profile', daily: 8, weekly: 55, monthly: 210, revenue: '₹42,000' },
        { test: 'Thyroid Function', daily: 6, weekly: 42, monthly: 165, revenue: '₹41,250' },
      ],
      chartKind: 'bar',
    },
  };

  // Fallback for any report not explicitly defined above
  const getReportDetail = (field, hint) => {
    return reportDetails[field] || {
      title: `${field} Report`,
      metrics: [
        { label: 'Primary Metric', value: '—', change: '+0%' },
        { label: 'Secondary Metric', value: '—', change: '0' },
        { label: 'Tertiary Metric', value: '—', change: '0' },
      ],
      chartData: [
        { name: 'Group A', value: 40, color: '#60a5fa' },
        { name: 'Group B', value: 32, color: '#34d399' },
        { name: 'Group C', value: 18, color: '#fbbf24' },
        { name: 'Group D', value: 10, color: '#f87171' },
      ],
      tableData: [
        { item: `${field} A`, value: 120, change: '+5%' },
        { item: `${field} B`, value: 95, change: '-2%' },
        { item: `${field} C`, value: 78, change: '+1%' },
      ],
      chartKind: (hint && /volume|revenue|count|trend/i.test(hint)) ? 'bar' : 'pie',
    };
  };

  // Grid meta for tabs/sections (unchanged list from your spec)
  const reportsData = [
    {
      title: 'Patient Reports',
      icon: '👥',
      sections: [
        { field: 'Patient Demographics', details: 'Age, Gender, Location' },
        { field: 'Patient Volume', details: 'Daily / Weekly / Monthly visits' },
        { field: 'New vs Returning Patients', details: 'Trend insights' },
        { field: 'Admission Types', details: 'OPD, IPD, Emergency' },
        { field: 'Referral Reports', details: 'Referred by doctor/partner' },
        { field: 'Follow-Up Reports', details: 'Upcoming, missed follow-ups' },
      ],
    },
    {
      title: 'Appointment & Doctor Reports',
      icon: '📅',
      sections: [
        { field: 'Appointment Trends', details: 'No. of visits by date/time' },
        { field: 'Doctor-wise Appointment Count', details: 'Consultations per doctor' },
        { field: 'Top Performing Doctors', details: 'Based on patients served & ratings' },
        { field: 'Missed / Cancelled Appointments', details: 'With reasons' },
        { field: 'Average Wait Time per Doctor', details: 'Patient experience metric' },
      ],
    },
    {
      title: 'Billing & Revenue Reports',
      icon: '💰',
      sections: [
        { field: 'Revenue by Source', details: 'Consultations, Pharmacy, Lab, Packages' },
        { field: 'Payment Mode Split', details: 'Cash, Card, UPI, Insurance' },
        { field: 'Pending Payments', details: 'Credit/Insurance outstanding' },
        { field: 'Refunds & Cancellations', details: 'Amount + reason' },
        { field: 'Profitability Report', details: 'Net vs operational cost' },
        { field: 'Tax Reports (GST/VAT)', details: 'Compliance-ready statements' },
      ],
    },
    {
      title: 'Pharmacy & Inventory Reports',
      icon: '💊',
      sections: [
        { field: 'Stock Status Report', details: 'Current quantity, batch, expiry' },
        { field: 'Low Stock / Out-of-Stock Report', details: 'Critical items' },
        { field: 'Expired / Wasted Medicines', details: 'With supplier link' },
        { field: 'Top-Selling Medicines', details: 'By value & volume' },
        { field: 'Purchase vs Sales Report', details: 'Inventory movement' },
        { field: 'Supplier-wise Purchase Report', details: 'Spend per vendor' },
        { field: 'GRN Reports', details: 'Goods receipt history' },
      ],
    },
    {
      title: 'Lab & Diagnostics Reports',
      icon: '🔬',
      sections: [
        { field: 'Test Volume Report', details: 'Total tests per day/week' },
        { field: 'Doctor-wise Test Referrals', details: 'Who ordered tests' },
        { field: 'Top Tests', details: 'Most commonly requested' },
        { field: 'Pending Reports', details: 'Not yet delivered' },
        { field: 'Revenue by Test Type', details: 'Financial breakdown' },
      ],
    },
    {
      title: 'Staff & HR Reports',
      icon: '👨‍⚕️',
      sections: [
        { field: 'Doctor Performance', details: 'Consultations, revenue contribution' },
        { field: 'Staff Attendance', details: 'Shift compliance' },
        { field: 'Payroll & Payout Reports', details: 'Salary, incentives' },
        { field: 'Utilization Rate', details: 'Staff-to-patient ratio' },
        { field: 'Feedback / Ratings', details: 'Doctor & nurse reviews' },
      ],
    },
    {
      title: 'Procurement & Supplier Reports',
      icon: '📦',
      sections: [
        { field: 'Supplier Performance', details: 'On-time delivery %, rejection rates' },
        { field: 'Spend by Supplier', details: 'Total procurement value' },
        { field: 'Outstanding POs', details: 'Pending deliveries' },
        { field: 'Purchase History', details: 'Vendor-wise orders' },
        { field: 'Cost Trends', details: 'Price fluctuations over time' },
      ],
    },
    {
      title: 'Master Admin (Multi-Clinic) Reports',
      icon: '🏥',
      sections: [
        { field: 'Clinic-wise Patient Volume', details: 'Compare branches' },
        { field: 'Clinic-wise Revenue Contribution', details: 'Financial split' },
        { field: 'Branch Occupancy & Utilization', details: 'Beds, OP/IP load' },
        { field: 'Top Performing Clinics', details: 'KPIs comparison' },
        { field: 'Network-wide Pharmacy Stock', details: 'Central visibility' },
        { field: 'Inter-branch Transfers', details: 'Stock/patient movement' },
      ],
    },
  ];

  // Simple visual renderers (CSS-only)
  const renderPieLegend = (data) => (
    <div className="bg-white p-4 rounded-lg border border-gray-200">
      <div className="flex justify-center items-center h-64">
        <div className="grid grid-cols-2 gap-4 w-full max-w-md">
          {data.map((item, i) => (
            <div key={i} className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color || '#60a5fa' }} />
              <div className="text-sm">
                <div className="font-medium">{item.name || item.category}</div>
                <div className="text-gray-600">{item.value ?? item.count}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBars = (data) => {
    const numeric = data.map(d => d.visits ?? d.revenue ?? d.count);
    const max = Math.max(...numeric, 1);
    return (
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="h-64 flex items-end justify-between space-x-2">
          {data.map((item, i) => {
            const v = item.visits ?? item.revenue ?? item.count ?? 0;
            const h = Math.max((v / max) * 200, 12);
            return (
              <div key={i} className="flex flex-col items-center flex-1">
                <div className="text-xs text-center mb-1 font-medium">{v}</div>
                <div className="bg-blue-500 w-full rounded-t" style={{ height: `${h}px` }} />
                <div className="text-xs text-center mt-2 text-gray-600">
                  {item.month || item.source || item.test || item.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Navigation handlers with two-step animation
  const openDetail = (field, hint) => {
    setAnim('leaving');
    setTimeout(() => {
      setSelectedReport(getReportDetail(field, hint));
      setView('detail');
      setAnim('entering');
      requestAnimationFrame(() => setTimeout(() => setAnim('idle'), 20));
    }, 200);
  };

  const backToGrid = () => {
    setAnim('leaving');
    setTimeout(() => {
      setView('grid');
      setSelectedReport(null);
      setAnim('entering');
      requestAnimationFrame(() => setTimeout(() => setAnim('idle'), 20));
    }, 200);
  };

  // Grid
  const renderGrid = () => (
    <div
      className={`transition-all duration-300 will-change-transform ${
        anim === 'leaving' ? 'opacity-0 -translate-x-6' : 'opacity-100 translate-x-0'
      }`}
    >
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
          <span className="text-2xl">{reportsData[activeTab].icon}</span>
          <span>{reportsData[activeTab].title}</span>
        </h2>
        <div className="h-1 w-20 bg-blue-500 rounded mt-2" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reportsData[activeTab].sections.map((section, i) => (
          <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
            <h3 className="font-semibold text-gray-800 mb-2 text-sm">{section.field}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{section.details}</p>
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                onClick={() => openDetail(section.field, section.details)}
                className="text-blue-600 hover:text-blue-800 text-xs font-medium transition-colors duration-200"
              >
                View Report →
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-blue-50 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 mb-2">{reportsData[activeTab].title} Summary</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full" />
            <span className="text-blue-700">
              {reportsData[activeTab].sections.length} Report Types Available
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span className="text-blue-700">Real-time Data Updates</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-purple-500 rounded-full" />
            <span className="text-blue-700">Export & Download Options</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Detail view
  const renderDetail = () => {
    if (!selectedReport) return null;
    const { title, metrics, chartData, tableData, chartKind } = selectedReport;
    return (
      <div
        className={`transition-all duration-300 will-change-transform ${
          anim === 'entering' ? 'opacity-0 translate-x-6' : 'opacity-100 translate-x-0'
        }`}
      >
        <div className="mb-6">
          <button
            onClick={backToGrid}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
          >
            <span>←</span>
            <span>Back to Reports</span>
          </button>
        </div>

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
          <div className="h-1 w-24 bg-blue-500 rounded mt-2" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {metrics.map((m, i) => (
            <div key={i} className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-600 font-medium">{m.label}</div>
              <div className="text-2xl font-bold text-blue-800 mt-1">{m.value}</div>
              <div className={`text-sm mt-1 ${String(m.change).startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                {m.change} vs last period
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Visual Analysis</h3>
            {chartKind === 'bar' ? renderBars(chartData) : renderPieLegend(chartData)}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Breakdown</h3>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {Object.keys(tableData[0] || { placeholder: '-' }).map((k, i) => (
                      <th
                        key={i}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {k.replace(/([A-Z])/g, ' $1').trim()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tableData.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      {Object.values(row).map((v, j) => (
                        <td key={j} className="px-4 py-3 text-sm text-gray-900">
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200">
            Export PDF
          </button>
          <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200">
            Export Excel
          </button>
          <button className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            Schedule Report
          </button>
          <button className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            Share Report
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
        <h1 className="text-2xl font-bold">Reports & Analytics</h1>
        <p className="text-gray-600 mt-1">Comprehensive healthcare analytics dashboard</p>
      </div>

      {/* Tabs are shown only in grid view */}
      {view === 'grid' && (
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap px-4">
            {reportsData.map((report, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`flex items-center space-x-2 px-4 py-3 font-medium text-sm transition-colors duration-200 border-b-2 ${
                  activeTab === i
                    ? 'border-blue-500 text-blue-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="text-lg">{report.icon}</span>
                <span className="whitespace-nowrap">{report.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6 min-h-[600px]">
        {view === 'grid' ? renderGrid() : renderDetail()}
      </div>
    </div>
  );
};

export default ClinicAnalytics;
